import { reactive, readonly } from 'vue'

// Define the keys for feature flags
export type FeatureFlagKey = 'ff_redesign_catalog' | 'ff_redesign_not_found' | 'ff_redesign_global'

// Interface for the state
interface FeatureFlagsState {
  [key: string]: boolean
}

// Initial state
const state = reactive<FeatureFlagsState>({
  ff_redesign_catalog: false,
  ff_redesign_not_found: false,
  ff_redesign_global: true
})

/**
 * Initialize feature flags from URL and LocalStorage
 */
const initFeatureFlags = () => {
  // Check if window is defined (SSR safety, though this is SPA)
  if (typeof window === 'undefined') return

  const urlParams = new URLSearchParams(window.location.search)
  console.log('[FeatureFlagService] Init from URL:', window.location.search)

  // List of all supported flags
  const flags: FeatureFlagKey[] = [
    'ff_redesign_catalog',
    'ff_redesign_not_found',
    'ff_redesign_global'
  ]

  flags.forEach((flag) => {
    // 1. Check URL (highest priority)
    // We check if the parameter exists at all.
    // If it exists but has no value (?ff_redesign_global), we treat it as true.
    // If it has value (?ff_redesign_global=true), we parse it.
    if (urlParams.has(flag)) {
      const paramValue = urlParams.get(flag)
      // If param is present without value (null string from get()) or 'true', enable it.
      // If explicitly 'false', disable it.
      let value = true
      if (paramValue === 'false') {
        value = false
      } else if (paramValue !== null && paramValue !== '' && paramValue !== 'true') {
        // If some other value is provided (e.g. ?ff=123), currently we default to true if it's not 'false'
        // But let's stick to strict 'true' or empty for enablement to be safe,
        // OR allow standard boolean-like values.
        // Let's adopt: empty or 'true' = true. 'false' = false. Anything else = false?
        // Let's keep existing logic but fix the empty value case.
        value = paramValue === 'true'
      }

      // FIX: Handle empty value (?ff_redesign_global) as true
      if (paramValue === '') value = true

      state[flag] = value
      console.log(`[FeatureFlagService] URL override: ${flag} = ${value}`)

      // Optional: Save to LS to persist the choice from URL
      localStorage.setItem(flag, String(value))
      return
    }

    // 2. Check LocalStorage
    const storedValue = localStorage.getItem(flag)
    if (storedValue !== null) {
      state[flag] = storedValue === 'true'
      return
    }

    // 3. Default (already set in initial state)
  })
}

// Initialize on load
initFeatureFlags()

/**
 * Composable to use feature flags
 */
export const useFeatureFlags = () => {
  return {
    flags: readonly(state),
    isEnabled: (key: FeatureFlagKey) => {
      // Global redesign flag overrides specific flags
      if (state.ff_redesign_global) {
        return true
      }
      return state[key] || false
    },
    setFlag: setFeatureFlag
  }
}

/**
 * Helper to manually set a flag (e.g. from console or dev UI)
 */
export const setFeatureFlag = (key: FeatureFlagKey, value: boolean) => {
  state[key] = value
  localStorage.setItem(key, String(value))
  console.log(`[FeatureFlagService] ${key} set to ${value}`)
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  // eslint-disable-next-line
  ;(window as any).FeatureFlagService = {
    state,
    set: setFeatureFlag
  }
}
